import { Badge, Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Card, Col, Container, Row, Spinner } from 'react-bootstrap';
import { checkIssueIsNewOrExisting } from '../../../lib/utils';
import { getProjectIssues } from '../../../modules/jira';
import { getDashboardIssueData } from '../../../modules/project';
import CustomAvatar from '../../elements/CustomAvatar';

const IssueComponent = ({ projects }) => {
  const [issueData, setIssueData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchIssueDetails = async (associations) => {
    const formattedIssues = [];

    for (const association of associations) {
      const issueIds =
        association?.issues?.map((issue) => issue.jira_issue_id) || [];
      const linkedProjectIds = association?.linked_projects?.map(p => p.linked_project_id);
      const jql = `project in (${linkedProjectIds.join(',')}) AND id in (${issueIds.join(',')})`;
      const bodyData = {
        jql,
        fields: ['summary', 'issuetype', 'status', 'created', 'reporter'],
        maxResults: issueIds?.length,
      };
      const issuesDetails = await getProjectIssues(
        bodyData,
        association.project_id
      );

      const formattedProjectIssues = issuesDetails.issues.map((issueDetail) => {
        const executionData = association.issues.find(
          (issue) => issue.jira_issue_id === issueDetail.id
        );
        const status = checkIssueIsNewOrExisting(
          issueDetail.fields.created,
          executionData.execution_date
        );
        return {
          id: executionData.id,
          jira_issue_id: issueDetail.id,
          key: issueDetail.key,
          siteUrl: association.jira_site_url,
          summary: issueDetail.fields.summary,
          issueType: issueDetail.fields.issuetype.name,
          issueTypeIcon: issueDetail.fields.issuetype.icon_url,
          issueStatus:
            issueDetail.fields.status.name ||
            issueDetail.fields.status.status_category.name,
          issueStatusColor:
            issueDetail.fields.status.status_category.color_name,
          issueCreatedAt: issueDetail.fields.created,
          reporter: issueDetail.fields.reporter.display_name,
          project: {
            project_id: association.project_id,
            project_name: association.project_name,
          },
          status,
        };
      });

      formattedIssues.push(...formattedProjectIssues);
    }

    setIssueData(formattedIssues);
  };

  const fetchTestCaseData = async () => {
    setLoading(true);
    try {
      const response = await getDashboardIssueData(projects);
      if (response.associations.length > 0) {
        await fetchIssueDetails(response.associations);
      }
    } catch (error) {
      setError(error.message || 'Failed to fetch data');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (projects && projects.length > 0) fetchTestCaseData();
  }, [projects]);

  return (
    <Container fluid>
      <Row>
        <Col>
          <h6 className="subheading-text">
            Logged in last 24 hours for executions
            {issueData && issueData?.length > 0 && ` (${issueData?.length})`}
          </h6>
        </Col>
      </Row>
      {loading ? (
        <Spinner
          style={{
            display: 'block',
            margin: 'auto',
            color: 'var(--primary-color)',
          }}
        />
      ) : issueData && issueData.length === 0 ? (
        <Row>
          <Col className="text-center">
            <Card>
              <Card.Body className="card-body-list">
                {error ? (
                  <Typography variant="body2" color="error">
                    {error}
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    No issues have been logged in the last 24 hours.
                  </Typography>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        <>
          {issueData &&
            issueData.map((issue) => (
              <Row
                key={issue.id}
                className="mt-2 cursor-pointer"
                onClick={() => {
                  const url = `${issue.siteUrl}/browse/${issue.key}`;
                  window.open(url, '_blank');
                }}>
                <Col>
                  <Card style={{ padding: '10px !important' }}>
                    <Card.Body className="card-body-list">
                      <Container fluid>
                        <Row className="d-flex justify-content-between align-items-center">
                          <Col md="6">
                            <Box className="d-flex align-items-center">
                              <Box>
                                <img
                                  src={issue.issueTypeIcon}
                                  title={issue.issueType}
                                  width="26px"
                                  alt={issue.issueType}
                                />
                              </Box>
                              <Box className="ml-2" sx={{ marginLeft: '16px' }}>
                                <Typography variant="subtitle1">
                                  {issue.summary}
                                </Typography>
                                <Typography
                                  variant="subtitle2"
                                  className="text-muted">
                                  {issue.key}
                                </Typography>
                              </Box>
                            </Box>
                          </Col>
                          <Col md="2" className="d-flex justify-content-center">
                            <Typography
                              variant="subtitle2"
                              className="text-muted">
                              {issue?.project?.project_name}
                            </Typography>
                          </Col>
                          <Col md="1" className="d-flex justify-content-center">
                            <Badge
                              title={
                                issue.status === 'New'
                                  ? 'New issue(Issue created after the execution)'
                                  : 'Existing issue(Issue created before the execution)'
                              }
                              sx={{
                                backgroundColor:
                                  issue.status === 'New'
                                    ? 'success.main'
                                    : 'primary.main',
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                              }}>
                              {issue.status}
                            </Badge>
                          </Col>
                          <Col
                            md="3"
                            className="d-flex justify-content-end gap-4">
                            <Typography
                              variant="subtitle2"
                              sx={{
                                border: '0.8px solid grey',
                                borderRadius: '4px',
                                padding: '4px 12px',
                                display: 'inline-block',
                              }}
                              className="text-muted">
                              {issue.issueStatus}
                            </Typography>
                            <CustomAvatar
                              firstName={issue.reporter}
                              size={36}
                            />
                          </Col>
                        </Row>
                      </Container>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            ))}
        </>
      )}
    </Container>
  );
};

export default IssueComponent;
